import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { 
  Shield, 
  Lock, 
  Eye, 
  Server, 
  CheckCircle,
  AlertTriangle,
  FileCheck,
  Users
} from 'lucide-react'

export function Security() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Security & Compliance - SourcedIn",
    "description": "Enterprise-grade security for RFP marketplace. SOC 2 certified, GDPR compliant, with end-to-end encryption and comprehensive audit trails."
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Enterprise Security & Compliance | SourcedIn RFP Marketplace"
        description="Bank-level security for your procurement data. SOC 2 Type II certified, GDPR compliant, end-to-end encryption, and comprehensive audit trails. Your data is always protected."
        keywords="RFP security, procurement data protection, SOC 2 compliance, GDPR compliant, enterprise security, data encryption, audit trails"
        canonical="https://sourcedin.ai/security"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Enterprise-Grade 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600"> Security</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Your procurement data deserves the highest level of protection. 
            We implement bank-level security measures to keep your sensitive information safe.
          </p>
        </div>
      </section>

      {/* Security Overview */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your Data is Our Priority
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive security measures protecting your most sensitive procurement information
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">End-to-End Encryption</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  AES-256 encryption for data at rest and TLS 1.3 for data in transit.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">SOC 2 Type II</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Independently audited and certified for security, availability, and confidentiality.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl">GDPR Compliant</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Full compliance with European data protection regulations and privacy rights.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">99.9% Uptime</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Enterprise-grade infrastructure with redundancy and disaster recovery.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Compliance Certifications */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Compliance & Certifications
            </h2>
            <p className="text-xl text-gray-600">
              Meeting the highest industry standards for security and compliance
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileCheck className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">SOC 2 Type II Certified</h3>
                  <p className="text-gray-600 mb-4">
                    Our security controls are independently audited and certified by a third-party 
                    auditor, ensuring we meet the highest standards for security, availability, 
                    processing integrity, confidentiality, and privacy.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Annual third-party audits</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Continuous monitoring</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">GDPR Compliance</h3>
                  <p className="text-gray-600 mb-4">
                    Full compliance with the General Data Protection Regulation, including 
                    data subject rights, lawful basis for processing, and data protection 
                    by design and by default.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Data subject rights</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Privacy by design</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Advanced Security Features
            </h2>
            <p className="text-xl text-gray-600">
              Multi-layered protection for your procurement data
            </p>
          </div>

          <div className="space-y-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Data Encryption</h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  All data is encrypted using industry-standard AES-256 encryption at rest 
                  and TLS 1.3 in transit. Your sensitive procurement information is protected 
                  with the same level of security used by major financial institutions.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">AES-256 encryption at rest</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">TLS 1.3 encryption in transit</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Key rotation and management</span>
                  </div>
                </div>
              </div>
              <Card className="p-8 border-0 shadow-lg bg-blue-50">
                <div className="text-center">
                  <Lock className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold mb-2">Bank-Level Security</h4>
                  <p className="text-gray-600">
                    The same encryption standards used by major banks and financial institutions.
                  </p>
                </div>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card className="p-8 border-0 shadow-lg bg-green-50 md:order-2">
                <div className="text-center">
                  <Eye className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold mb-2">Complete Audit Trail</h4>
                  <p className="text-gray-600">
                    Every action is logged and monitored for complete transparency and compliance.
                  </p>
                </div>
              </Card>
              <div className="md:order-1">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Access Control & Monitoring</h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Comprehensive access controls with role-based permissions, multi-factor 
                  authentication, and continuous monitoring of all system activities. 
                  Complete audit trails ensure full visibility and compliance.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Multi-factor authentication</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Role-based access control</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Complete audit logging</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Security */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Infrastructure Security
            </h2>
            <p className="text-xl text-gray-600">
              Enterprise-grade infrastructure with multiple layers of protection
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Cloud Infrastructure</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Hosted on AWS with enterprise-grade security, redundancy, and 99.9% uptime SLA.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">Network Security</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Advanced firewalls, DDoS protection, and network segmentation for maximum security.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Threat Detection</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  24/7 monitoring with AI-powered threat detection and automated response systems.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Privacy & Data Protection */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Privacy & Data Protection
            </h2>
            <p className="text-xl text-gray-600">
              Your privacy rights are protected at every level
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 border-0 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Data Minimization</h3>
              <p className="text-gray-600 mb-4">
                We collect and process only the data necessary to provide our services. 
                No unnecessary data collection or sharing with third parties.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Minimal data collection</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">No unnecessary sharing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Purpose limitation</span>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Data Subject Rights</h3>
              <p className="text-gray-600 mb-4">
                Full support for GDPR data subject rights including access, rectification, 
                erasure, and data portability.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Right to access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Right to rectification</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Right to erasure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Data portability</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Security Team */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Dedicated Security Team
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our security experts work around the clock to protect your data and maintain 
            the highest security standards.
          </p>
          <Card className="p-8 border-0 shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <Users className="h-16 w-16 text-blue-600" />
            </div>
            <p className="text-lg text-gray-700">
              Have security questions or need to report a vulnerability? 
              Contact our security team directly at{' '}
              <a href="mailto:security@sourcedin.ai" className="text-blue-600 hover:underline">
                security@sourcedin.ai
              </a>
            </p>
          </Card>
        </div>
      </section>
    </div>
  )
}
